<template>
  <v-sheet dark class="alibey-bills">
    <div class="bill-content">
    <header class="bill-header">
      <div><p class="bill-eyebrow">Buchhaltung</p><h1>Alibey</h1><p class="bill-subtitle">Buchungen, Rechnungen und Zahlungen im Überblick.</p></div>
      <v-btn outlined :loading="loading" :disabled="busy" @click="load"><v-icon left>mdi-refresh</v-icon>Aktualisieren</v-btn>
    </header>

    <v-alert v-if="loadError" type="error" outlined>{{ loadError }}</v-alert>
    <div class="bill-stats" aria-label="Aktuelle Auswahl">
      <article><span class="bill-stat-icon bill-icon-blue"><v-icon>mdi-airplane</v-icon></span><div><span>Buchungen</span><strong>{{ filtered.length }}</strong><small>Aktuelle Auswahl</small></div></article>
      <article><span class="bill-stat-icon"><v-icon>mdi-file-document-outline</v-icon></span><div><span>Ohne Rechnung</span><strong>{{ count('none') }}</strong><small>Noch nicht abgerechnet</small></div></article>
      <article><span class="bill-stat-icon bill-icon-amber"><v-icon>mdi-clock-outline</v-icon></span><div><span>Offene Rechnungen</span><strong>{{ count('open') }}</strong><small>{{ money(openCents) }} erfasst<span v-if="unknownOpen"> · {{ unknownOpen }} ohne Betrag</span></small></div></article>
      <article><span class="bill-stat-icon bill-icon-green"><v-icon>mdi-cash-check</v-icon></span><div><span>Bezahlt</span><strong>{{ count('paid') }}</strong><small>Manuell bestätigt</small></div></article>
    </div>

    <section class="bill-panel">
      <div class="bill-filters">
        <v-text-field dark color="#58d388" v-model="filters.search" class="bill-search" label="Name, Rechnungsnummer oder Referenz suchen" prepend-inner-icon="mdi-magnify" outlined dense clearable hide-details />
        <v-select dark color="#58d388" :menu-props="{ dark: true }" v-model="filters.year" :items="years" label="Reisejahr" outlined dense clearable hide-details />
        <v-select dark color="#58d388" :menu-props="{ dark: true }" v-model="filters.status" :items="statuses" label="Zahlungsstatus" outlined dense clearable hide-details />
        <v-select dark color="#58d388" :menu-props="{ dark: true }" v-model="filters.sent" :items="sentOptions" label="Rechnungsversand" outlined dense clearable hide-details />
      </div>
      <div class="bill-filter-extra">
        <v-select dark color="#58d388" :menu-props="{ dark: true }" v-model="filters.room" :items="rooms" label="Zimmer" outlined dense clearable hide-details />
        <v-text-field dark color="#58d388" v-model="filters.from" type="date" label="Reisezeitraum von" outlined dense clearable hide-details />
        <v-text-field dark color="#58d388" v-model="filters.to" type="date" label="Reisezeitraum bis" outlined dense clearable hide-details />
        <v-btn text :disabled="!hasFilters" @click="resetFilters">Filter zurücksetzen</v-btn>
      </div>
      <v-data-table dark :headers="headers" :items="tableRows" item-key="id" :loading="loading" :items-per-page="15" :mobile-breakpoint="800"
        :footer-props="{ 'items-per-page-options': [15, 30, 60], 'items-per-page-text': 'Buchungen pro Seite' }"
        sort-by="bookedAt" :sort-desc="true" no-data-text="Keine Buchungen für diese Auswahl." loading-text="Buchungen werden geladen …">
        <template v-slot:item.name="{ item }">
          <button type="button" class="bill-name" @click="detail = item">{{ item.name || 'Name fehlt' }}</button>
          <span class="bill-meta">{{ tripOf(item).persons.join(', ') }}</span>
          <span v-if="item.missingBooking" class="bill-meta">Ursprüngliche Buchung nicht mehr verfügbar</span>
          <span v-if="item.deletionPending" class="bill-meta">Löschung noch nicht abgeschlossen. Bitte erneut versuchen.</span>
        </template>
        <template v-slot:item.bookedAt="{ item }">
          <span>{{ dateTime(item.bookedAt) }}</span>
        </template>
        <template v-slot:item.start="{ item }">
          <span>{{ date(tripOf(item).start) }} – {{ date(tripOf(item).end) }}</span>
          <span class="bill-meta">{{ tripOf(item).room }}<span v-if="tripOf(item).subtitle"> · {{ tripOf(item).subtitle }}</span></span>
          <span class="bill-meta">{{ tripOf(item).nights || '–' }} Nächte · {{ tripOf(item).persons.length }} Personen</span>
        </template>
        <template v-slot:item.amount="{ item }">
          <span class="bill-amount">{{ money(item.amount) }}</span>
          <span v-if="!item.bill" class="bill-meta">{{ item.priceError ? 'Preis prüfen' : 'Vorgeschlagener Betrag' }}</span>
          <span v-else-if="item.bill.invoice && item.bill.invoice.pricing.adjusted" class="bill-meta">Manuell angepasst</span>
          <span v-else-if="item.amount == null" class="bill-meta">Betrag nicht erfasst</span>
        </template>
        <template v-slot:item.payment="{ item }">
          <span class="bill-badge" :class="'bill-badge-' + statusOf(item)">{{ item.payment }}</span>
          <span v-if="item.bill && item.bill.paidAt" class="bill-meta">Markiert am {{ dateTime(item.bill.paidAt) }}</span>
        </template>
        <template v-slot:item.delivery="{ item }">
          <span>{{ item.delivery }}</span>
          <span v-if="item.bill && item.bill.kind === 'external'" class="bill-meta">Bisherige Rechnung</span>
          <span v-if="referenceOf(item)" class="bill-reference">{{ referenceOf(item) }}</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="bill-actions">
            <v-btn v-if="!item.bill" small color="#009640" depressed :disabled="busy || loading || !!loadError || item.deletionPending" @click="openInvoice(item)"><v-icon left small>mdi-file-document-plus-outline</v-icon>Rechnung erstellen</v-btn>
            <v-btn v-if="item.bill && item.bill.kind === 'generated'" small outlined :loading="pdfId === item.id" :disabled="busy || !!pdfId" @click="download(item)"><v-icon left small>mdi-download</v-icon>PDF herunterladen</v-btn>
            <v-btn small text :disabled="busy || loading || !!loadError || item.deletionPending" @click="openStatus(item)">{{ item.bill ? 'Status ändern' : 'Rechnung bereits versendet' }}</v-btn>
            <v-btn v-if="!item.bill || !item.bill.isPaid" small text color="#58d388" :disabled="busy || loading || !!loadError || item.deletionPending" @click="openStatus(item, true)">Als bezahlt markieren</v-btn>
            <v-btn v-if="!item.bill && !item.missingBooking" small text color="#fca5a5" :disabled="busy || loading || !!loadError" @click="openDelete(item)"><v-icon left small>mdi-delete-outline</v-icon>Buchung löschen</v-btn>
          </div>
        </template>
      </v-data-table>
    </section>
    </div>

    <v-dialog v-model="invoiceDialog" max-width="760" persistent scrollable>
      <v-card dark class="bill-dialog" v-if="selected">
        <v-card-title><h2>Rechnung erstellen</h2><v-spacer /><v-btn icon :disabled="busy" aria-label="Schliessen" @click="invoiceDialog = false"><v-icon>mdi-close</v-icon></v-btn></v-card-title>
        <v-card-text>
          <div class="bill-trip"><strong>{{ selected.trip.room }}</strong><span>{{ date(selected.trip.start) }} – {{ date(selected.trip.end) }} · {{ selected.trip.nights }} Nächte</span><span>{{ selected.trip.persons.join(', ') }}</span></div>
          <v-alert v-if="formError" type="error" outlined>{{ formError }}</v-alert>
          <v-alert v-if="selected.priceError" type="warning" outlined>{{ selected.priceError }}</v-alert>
          <v-form ref="invoiceForm" @submit.prevent="createInvoice">
            <h3>Rechnungsempfänger</h3>
            <div class="bill-form-grid">
              <v-text-field dark color="#58d388" v-model="form.recipient.name" label="Vor- und Nachname" class="bill-full" outlined dense :rules="[required]" maxlength="70" />
              <v-text-field dark color="#58d388" v-model="form.recipient.extra" label="Adresszusatz (optional)" class="bill-full" outlined dense maxlength="70" />
              <v-text-field dark color="#58d388" v-model="form.recipient.address" label="Strasse" outlined dense :rules="[required]" maxlength="70" />
              <v-text-field dark color="#58d388" v-model="form.recipient.buildingNumber" label="Hausnummer" outlined dense maxlength="16" />
              <v-text-field dark color="#58d388" v-model="form.recipient.zip" label="PLZ" outlined dense :rules="[required]" maxlength="16" />
              <v-text-field dark color="#58d388" v-model="form.recipient.city" label="Ort" outlined dense :rules="[required]" maxlength="35" />
              <v-text-field dark color="#58d388" v-model="form.recipient.country" label="Ländercode" hint="Zum Beispiel CH, DE oder AT" persistent-hint outlined dense :rules="[countryRule]" maxlength="2" />
            </div>
            <h3>Rechnungsbetrag</h3>
            <dl class="bill-breakdown">
              <div><dt>Pauschalreise · {{ selected.trip.nights }} Nächte</dt><dd>{{ money(selected.pricing.baseCents) }}</dd></div>
            </dl>
            <v-switch v-model="form.adjustAmount" label="Betrag manuell anpassen" color="#58d388" hide-details class="bill-adjust-switch" />
            <div v-if="form.adjustAmount" class="bill-form-grid bill-adjust-fields">
              <v-text-field dark color="#58d388" v-model="form.amount" label="Neuer Gesamtbetrag" suffix="CHF" inputmode="decimal" outlined dense :rules="[amountRule]" />
              <v-text-field dark color="#58d388" v-model="form.adjustmentReason" label="Grund, z. B. Flugkosten oder Rabatt" outlined dense :rules="[required]" maxlength="140" />
            </div>
            <div class="bill-total"><span>Gesamtbetrag</span><strong>{{ money(invoiceAmount) }}</strong></div>
            <p class="bill-note">Zahlbar innert 30 Tagen. Inklusive Sportpaket, ohne Flugkosten, sofern diese nicht manuell ergänzt werden.</p>
            <p class="bill-note">Die Rechnung wird mit diesen Angaben gespeichert. Danach bleiben Betrag, Empfänger und Referenz unverändert.</p>
          </v-form>
        </v-card-text>
        <v-card-actions><v-btn text :disabled="busy" @click="invoiceDialog = false">Abbrechen</v-btn><v-spacer /><v-btn color="#009640" depressed :loading="busy" :disabled="invoiceAmount == null" @click="createInvoice">Erstellen und PDF herunterladen</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="statusDialog" max-width="580" persistent>
      <v-card dark v-if="selected" class="bill-dialog">
        <v-card-title><h2>{{ selected.bill ? 'Rechnungsstatus' : 'Bestehende Rechnung erfassen' }}</h2><v-spacer /><v-btn icon :disabled="busy" aria-label="Schliessen" @click="statusDialog = false"><v-icon>mdi-close</v-icon></v-btn></v-card-title>
        <v-card-text>
          <p><strong>{{ recipientOf(selected).name }}</strong><br />{{ date(tripOf(selected).start) }} – {{ date(tripOf(selected).end) }}</p>
          <v-alert v-if="formError" type="error" outlined>{{ formError }}</v-alert>
          <v-checkbox v-model="statusForm.isSent" label="Rechnung bereits versendet" hide-details color="#58d388" />
          <v-checkbox v-model="statusForm.isPaid" label="Rechnung bezahlt" hide-details color="#58d388" />
          <p class="bill-note bill-status-note">Ohne Häkchen bei «bezahlt» bleibt die Rechnung offen. Hier wird der Status dokumentiert; es wird keine E-Mail versendet.</p>
          <template v-if="!selected.bill || selected.bill.kind === 'external'">
            <p class="bill-note">Die bisherige Rechnung wird übernommen. Es wird keine zusätzliche Rechnung erstellt.</p>
            <v-text-field dark color="#58d388" v-model="statusForm.reference" label="Bisherige Referenz oder Rechnungsnummer (optional)" outlined dense maxlength="80" />
            <v-text-field dark color="#58d388" v-model="statusForm.amount" label="Bisheriger Rechnungsbetrag (optional)" suffix="CHF" inputmode="decimal" outlined dense />
          </template>
        </v-card-text>
        <v-card-actions><v-btn text :disabled="busy" @click="statusDialog = false">Abbrechen</v-btn><v-spacer /><v-btn color="#009640" depressed :loading="busy" @click="saveStatus">Speichern</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="580" persistent>
      <v-card dark v-if="selected" class="bill-dialog">
        <v-card-title><h2>Buchung löschen?</h2><v-spacer /><v-btn icon :disabled="busy" aria-label="Schliessen" @click="deleteDialog = false"><v-icon>mdi-close</v-icon></v-btn></v-card-title>
        <v-card-text>
          <v-alert v-if="formError" type="error" outlined>{{ formError }}</v-alert>
          <div class="bill-trip"><strong>{{ recipientOf(selected).name }}</strong><span>{{ tripOf(selected).persons.join(', ') }}</span><span>{{ date(tripOf(selected).start) }} – {{ date(tripOf(selected).end) }}</span><span>{{ tripOf(selected).room }}</span></div>
          <p>Diese Buchung wird aus der Alibey-Buchungsliste entfernt. Das kann nicht rückgängig gemacht werden.</p>
          <p class="bill-note">Löschen ist nur möglich, solange keine Rechnung erstellt oder als bestehende Rechnung erfasst wurde.</p>
        </v-card-text>
        <v-card-actions><v-btn text :disabled="busy" @click="deleteDialog = false">Abbrechen</v-btn><v-spacer /><v-btn color="#b91c1c" depressed :loading="busy" @click="deleteBooking">Buchung endgültig löschen</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog :value="!!detail" max-width="650" scrollable @input="closeDetail">
      <v-card dark v-if="detail" class="bill-dialog">
        <v-card-title><h2>Buchungsdetails</h2><v-spacer /><v-btn icon aria-label="Schliessen" @click="detail = null"><v-icon>mdi-close</v-icon></v-btn></v-card-title>
        <v-card-text>
          <h3>{{ recipientOf(detail).name }}</h3>
          <p><template v-if="recipientOf(detail).extra">{{ recipientOf(detail).extra }}<br /></template>{{ recipientOf(detail).address }} {{ recipientOf(detail).buildingNumber }}<br />{{ recipientOf(detail).zip }} {{ recipientOf(detail).city }} · {{ recipientOf(detail).country }}</p>
          <p>Buchungsdatum: {{ dateTime(bookingTime(detail.createdAt)) }}</p>
          <div class="bill-trip"><strong>{{ tripOf(detail).room }}</strong><span>{{ tripOf(detail).subtitle }}</span><span>{{ date(tripOf(detail).start) }} – {{ date(tripOf(detail).end) }} · {{ tripOf(detail).nights }} Nächte</span><span>{{ tripOf(detail).persons.join(', ') }}</span></div>
          <div v-if="detail.bill && detail.bill.invoice">
            <h3>{{ detail.bill.invoice.number }}</h3>
            <p>Erstellt am {{ date(detail.bill.invoice.issuedOn) }}<br />Zahlbar bis {{ date(detail.bill.invoice.dueOn) }}</p>
            <p v-if="detail.bill.invoice.pricing.adjusted">Betragsanpassung: {{ detail.bill.invoice.pricing.adjustmentReason }}</p>
          </div>
          <p v-if="referenceOf(detail)">Referenz<br /><span class="bill-reference">{{ referenceOf(detail) }}</span></p>
          <div class="bill-total"><span>{{ detail.bill ? 'Rechnungsbetrag' : 'Vorgeschlagener Betrag' }}</span><strong>{{ money(amountOf(detail)) }}</strong></div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
import api from "@/api"
import { tripOf, recipientOf, referenceOf, amountOf, statusOf, filterRows, parseAmount } from "./alibeyBillView"

const emptyFilters = () => ({ search: "", year: null, room: null, status: null, sent: null, from: "", to: "" })
export default {
  name: "AlibeyBills",
  data() {
    return {
      rows: [], loading: false, loadError: "", busy: false, pdfId: null, filters: emptyFilters(),
      selected: null, detail: null, invoiceDialog: false, statusDialog: false, deleteDialog: false, formError: "",
      form: { recipient: {}, adjustAmount: false, amount: "", adjustmentReason: "" },
      statusForm: { isSent: false, isPaid: false, reference: "", amount: "" },
      statuses: [{ text: "Ohne Rechnung", value: "none" }, { text: "Offen", value: "open" }, { text: "Bezahlt", value: "paid" }],
      sentOptions: [{ text: "Bereits versendet", value: "yes" }, { text: "Noch nicht versendet", value: "no" }],
      headers: [
        { text: "Buchung", value: "name" }, { text: "Buchungsdatum", value: "bookedAt" }, { text: "Reise", value: "start" }, { text: "Betrag", value: "amount" },
        { text: "Zahlung", value: "payment" }, { text: "Rechnung", value: "delivery" }, { text: "Aktionen", value: "actions", sortable: false },
      ],
    }
  },
  computed: {
    filtered() { return filterRows(this.rows, this.filters) },
    tableRows() { return this.filtered.map(row => ({ ...row, name: recipientOf(row).name, start: tripOf(row).start || "", amount: amountOf(row),
      bookedAt: this.bookingTime(row.createdAt),
      payment: statusOf(row) === "paid" ? "Bezahlt" : statusOf(row) === "open" ? "Offen" : "Ohne Rechnung",
      delivery: !row.bill ? "Noch keine Rechnung" : row.bill.isSent ? "Versendet" : "Nicht versendet" })) },
    years() { return [...new Set(this.rows.map(row => (tripOf(row).start || "").slice(0, 4)).filter(Boolean))].sort().reverse() },
    rooms() { return [...new Set(this.rows.map(row => tripOf(row).room).filter(Boolean))].sort((a, b) => a.localeCompare(b, "de-CH")) },
    hasFilters() { return Object.values(this.filters).some(Boolean) },
    openCents() { return this.filtered.filter(row => statusOf(row) === "open").reduce((sum, row) => sum + (amountOf(row) || 0), 0) },
    unknownOpen() { return this.filtered.filter(row => statusOf(row) === "open" && amountOf(row) == null).length },
    invoiceAmount() { return !this.selected ? null : this.form.adjustAmount ? parseAmount(this.form.amount) : this.selected.pricing.baseCents },
  },
  created() { this.load() },
  methods: {
    tripOf, recipientOf, referenceOf, amountOf, statusOf,
    required(value) { return !!String(value || "").trim() || "Bitte ausfüllen." },
    countryRule(value) { return /^[A-Za-z]{2}$/.test(value || "") || "Zweistelliger Ländercode, z. B. CH." },
    amountRule(value) { return parseAmount(value) != null || "Bitte einen gültigen Betrag grösser als 0 eingeben." },
    count(status) { return this.filtered.filter(row => statusOf(row) === status).length },
    money(value) { return value == null ? "–" : `CHF ${(value / 100).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
    date(value) { return value ? value.slice(0, 10).split("-").reverse().join(".") : "–" },
    dateTime(value) { return value ? new Date(value).toLocaleDateString("de-CH", { timeZone: "Europe/Zurich" }) : "–" },
    bookingTime(value) {
      const time = Number(value) * 1000
      return time > 0 && Number.isFinite(new Date(time).getTime()) ? time : null
    },
    resetFilters() { this.filters = emptyFilters() },
    closeDetail(value) { if (!value) this.detail = null },
    async errorMessage(error) {
      const response = error.response
      if (response && [401, 403].includes(response.status)) return "Deine Sitzung oder Buchhaltungsberechtigung ist nicht mehr gültig."
      let data = response && response.data
      if (typeof Blob !== "undefined" && data instanceof Blob) {
        try { data = JSON.parse(await data.text()) } catch (_) { data = null }
      }
      return data && data.message || "Die Anfrage ist fehlgeschlagen. Bitte die Übersicht aktualisieren und erneut versuchen."
    },
    async load() {
      this.loading = true
      this.loadError = ""
      try {
        const latestYear = this.years[0] || null
        const { rows } = await api.alibeyBills.fetch()
        const useLatestYear = this.filters.year === latestYear
        this.rows = rows
        if (useLatestYear) this.filters.year = this.years[0] || null
      } catch (error) {
        this.loadError = await this.errorMessage(error)
        if (error.response && [401, 403].includes(error.response.status)) this.rows = []
      } finally { this.loading = false }
    },
    openDelete(row) {
      if (this.busy || this.loading || this.loadError || row.bill || row.missingBooking) return
      this.selected = row
      this.formError = ""
      this.deleteDialog = true
    },
    async deleteBooking() {
      if (this.busy || !this.deleteDialog || !this.selected || this.selected.bill || this.selected.missingBooking) return
      this.busy = true
      this.formError = ""
      try {
        const id = this.selected.id
        await api.alibeyBills.remove(id, { fingerprint: this.selected.fingerprint })
        const useLatestYear = this.filters.year === (this.years[0] || null)
        this.rows = this.rows.filter(row => row.id !== id)
        if (useLatestYear) this.filters.year = this.years[0] || null
        if (this.detail && this.detail.id === id) this.detail = null
        this.deleteDialog = false
        this.selected = null
        this.$toast.success("Buchung wurde gelöscht.")
      } catch (error) {
        this.formError = await this.errorMessage(error)
        if (error.response && [404, 409].includes(error.response.status)) {
          this.deleteDialog = false
          this.$toast.error(this.formError)
          await this.load()
        }
      } finally { this.busy = false }
    },
    openInvoice(row) {
      this.selected = row
      this.formError = ""
      this.form = { recipient: { ...row.recipient }, adjustAmount: false,
        amount: row.pricing.baseCents == null ? "" : (row.pricing.baseCents / 100).toFixed(2), adjustmentReason: "" }
      this.invoiceDialog = true
      this.$nextTick(() => { if (this.$refs.invoiceForm) this.$refs.invoiceForm.resetValidation() })
    },
    updateBill(id, bill) {
      const row = this.rows.find(item => item.id === id)
      if (row) this.$set(row, "bill", bill)
      if (this.selected && this.selected.id === id) this.$set(this.selected, "bill", bill)
    },
    async createInvoice() {
      if (this.busy || !this.$refs.invoiceForm.validate() || this.invoiceAmount == null) return
      this.busy = true
      this.formError = ""
      try {
        const result = await api.alibeyBills.create(this.selected.id, { fingerprint: this.selected.fingerprint, recipient: this.form.recipient,
          adjustAmount: this.form.adjustAmount, amountCents: this.invoiceAmount, adjustmentReason: this.form.adjustmentReason })
        this.updateBill(this.selected.id, result.bill)
        this.invoiceDialog = false
        this.$toast.success("Rechnung wurde gespeichert.")
        await this.download(this.selected)
      } catch (error) {
        this.formError = await this.errorMessage(error)
        if (error.response && error.response.status === 409) {
          this.invoiceDialog = false
          this.$toast.error(this.formError)
          await this.load()
        }
      } finally { this.busy = false }
    },
    openStatus(row, paid = false) {
      this.selected = row
      this.formError = ""
      this.statusForm = { isSent: row.bill ? row.bill.isSent : !paid, isPaid: paid || !!(row.bill && row.bill.isPaid),
        reference: row.bill && row.bill.externalReference || "",
        amount: row.bill && row.bill.externalAmountCents != null ? (row.bill.externalAmountCents / 100).toFixed(2) : "" }
      this.statusDialog = true
    },
    async saveStatus() {
      if (this.busy) return
      const amount = this.statusForm.amount.trim()
      if (amount && parseAmount(amount) == null) { this.formError = "Bitte einen gültigen Rechnungsbetrag eingeben oder das Feld leer lassen."; return }
      this.busy = true
      this.formError = ""
      try {
        const result = await api.alibeyBills.status(this.selected.id, { revision: this.selected.bill ? this.selected.bill.revision : null,
          fingerprint: this.selected.fingerprint, isSent: this.statusForm.isSent, isPaid: this.statusForm.isPaid,
          externalReference: this.statusForm.reference, externalAmountCents: amount ? parseAmount(amount) : null })
        this.updateBill(this.selected.id, result.bill)
        this.statusDialog = false
        this.$toast.success("Rechnungsstatus gespeichert.")
      } catch (error) {
        this.formError = await this.errorMessage(error)
        if (error.response && error.response.status === 409) {
          this.statusDialog = false
          this.$toast.error(this.formError)
          await this.load()
        }
      } finally { this.busy = false }
    },
    async download(row) {
      if (this.pdfId) return
      this.pdfId = row.id
      try { await api.alibeyBills.download(row.id, row.bill.invoice.number) }
      catch (error) { this.$toast.error(`PDF konnte nicht heruntergeladen werden. ${await this.errorMessage(error)}`) }
      finally { this.pdfId = null }
    },
  },
}
</script>

<style scoped>
.alibey-bills, .bill-dialog {
  --bill-ink: #f4f4f5;
  --bill-soft: #d4d4d8;
  --bill-muted: #a3a3a8;
  --bill-border: rgba(255, 255, 255, .1);
  --bill-surface: rgba(255, 255, 255, .035);
  --bill-accent: #58d388;
  color-scheme: dark;
  color: var(--bill-ink);
  font-size: 1rem;
}
.alibey-bills { padding: 1.5rem; min-height: 100vh; background: radial-gradient(circle at 8% 0%, rgba(0, 150, 64, .075), transparent 24rem), #0f0f10; }
.bill-content { max-width: 92.5rem; margin: 0 auto; }
.bill-header { display: flex; justify-content: space-between; align-items: center; gap: 1.5rem; margin-bottom: 1.5rem; }
.bill-eyebrow { margin: 0 0 .375rem; font-size: .75rem; font-weight: 700; text-transform: uppercase; letter-spacing: .12em; color: var(--bill-accent); }
.bill-header h1 { margin: 0; font-size: 2rem; line-height: 1.2; font-weight: 700; letter-spacing: -.025em; }
.bill-subtitle { margin: .625rem 0 0; color: var(--bill-muted); font-size: 1rem; }
.bill-header ::v-deep .v-btn { min-height: 2.75rem; border-color: var(--bill-border); border-radius: .8125rem; background: rgba(255, 255, 255, .055); }
.bill-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: .75rem; margin-bottom: 1rem; }
.bill-stats article { display: flex; align-items: center; min-width: 0; min-height: 6.75rem; padding: 1.125rem; gap: .875rem; border: 1px solid var(--bill-border); border-radius: 1.1875rem; background: var(--bill-surface); box-shadow: 0 .75rem 1.875rem rgba(0, 0, 0, .1); }
.bill-stats article > div { display: grid; min-width: 0; gap: .25rem; }
.bill-stats article > div > span { font-size: .875rem; color: var(--bill-soft); font-weight: 600; }
.bill-stats strong { font-size: 1.75rem; font-weight: 600; line-height: 1.25; font-variant-numeric: tabular-nums; }
.bill-stats small { font-size: .75rem; color: var(--bill-muted); line-height: 1.5; overflow-wrap: anywhere; }
.bill-stat-icon { display: grid; place-items: center; flex: 0 0 2.625rem; width: 2.625rem; height: 2.625rem; border-radius: .8125rem; color: #c5c5c8; background: rgba(255, 255, 255, .065); }
.bill-stat-icon ::v-deep .v-icon { color: inherit; }
.bill-icon-blue { color: #7dd3fc; background: rgba(14, 165, 233, .12); }
.bill-icon-amber { color: #fcd34d; background: rgba(245, 158, 11, .12); }
.bill-icon-green { color: var(--bill-accent); background: rgba(0, 150, 64, .12); }
.bill-panel { border: 1px solid var(--bill-border); border-radius: 1.375rem; background: rgba(255, 255, 255, .03); overflow: hidden; box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, .12); }
.bill-filters { display: grid; grid-template-columns: minmax(280px, 2fr) repeat(3, minmax(150px, 1fr)); gap: .875rem; padding: 1.25rem 1.25rem .875rem; }
.bill-filter-extra { display: grid; grid-template-columns: minmax(200px, 1.4fr) repeat(2, minmax(160px, 1fr)) auto; gap: .875rem; padding: 0 1.25rem 1.25rem; align-items: center; border-bottom: 1px solid var(--bill-border); }
.bill-name { color: var(--bill-accent); font-size: 1rem; font-weight: 600; text-align: left; text-decoration: underline; text-underline-offset: .2em; }
.bill-name:focus-visible { outline: 2px solid var(--bill-accent); outline-offset: 3px; }
.bill-meta { display: block; color: var(--bill-muted); font-size: .875rem; line-height: 1.45; margin-top: .25rem; }
.bill-amount { font-variant-numeric: tabular-nums; white-space: nowrap; font-weight: 600; }
.bill-badge { display: inline-block; border: 1px solid transparent; border-radius: 2rem; padding: .25rem .625rem; font-size: .75rem; line-height: 1.5; font-weight: 600; white-space: nowrap; }
.bill-badge-none { background: rgba(255, 255, 255, .055); border-color: var(--bill-border); color: var(--bill-soft); }
.bill-badge-open { background: rgba(245, 158, 11, .1); border-color: rgba(245, 158, 11, .22); color: #fcd34d; }
.bill-badge-paid { background: rgba(0, 150, 64, .14); border-color: rgba(0, 150, 64, .3); color: #7ce2a5; }
.bill-reference { display: block; overflow-wrap: anywhere; color: var(--bill-soft); font-size: .875rem; line-height: 1.5; font-variant-numeric: tabular-nums; user-select: text; margin-top: .25rem; }
.bill-actions { display: flex; flex-direction: column; align-items: flex-start; gap: .375rem; padding: .5rem 0; }
.alibey-bills ::v-deep .v-data-table { color: var(--bill-ink); background: rgba(0, 0, 0, .08); }
.alibey-bills ::v-deep .v-data-table__wrapper { overflow-x: auto; }
.alibey-bills ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td { font-size: 1rem; padding-top: 1rem; padding-bottom: 1rem; border-bottom-color: rgba(255, 255, 255, .055); }
.alibey-bills ::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th { height: 3.25rem; font-size: .75rem; font-weight: 700; color: var(--bill-muted); background: rgba(255, 255, 255, .025); border-bottom-color: var(--bill-border); letter-spacing: .045em; text-transform: uppercase; }
.alibey-bills ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover { background: rgba(255, 255, 255, .035); }
.alibey-bills ::v-deep .v-btn { font-size: .875rem; border-radius: .625rem; text-transform: none; letter-spacing: 0; }
.alibey-bills ::v-deep .v-btn--outlined { border-color: rgba(255, 255, 255, .2); }
.alibey-bills ::v-deep .v-label, .alibey-bills ::v-deep input, .alibey-bills ::v-deep .v-select__selection { font-size: 1rem; }
.alibey-bills ::v-deep .v-data-footer { border-top-color: var(--bill-border); font-size: .875rem; color: var(--bill-soft); }
.alibey-bills ::v-deep .v-data-footer__select { font-size: .875rem; }
.alibey-bills ::v-deep .v-text-field--outlined > .v-input__control > .v-input__slot,
.bill-dialog ::v-deep .v-text-field--outlined > .v-input__control > .v-input__slot { border-radius: .8125rem; background: rgba(255, 255, 255, .025); }
.alibey-bills ::v-deep .v-text-field--outlined:not(.v-input--is-focused):not(.error--text) fieldset,
.bill-dialog ::v-deep .v-text-field--outlined:not(.v-input--is-focused):not(.error--text) fieldset { border-color: rgba(255, 255, 255, .16); }
.alibey-bills ::v-deep .v-text-field input[type="date"] { color-scheme: dark; }
.bill-dialog { border: 1px solid var(--bill-border); border-radius: 1.375rem; background: #19191b; }
.bill-dialog h2 { font-size: 1.25rem; line-height: 1.4; font-weight: 600; margin: 0; overflow-wrap: anywhere; }
.bill-dialog h3 { font-size: 1rem; font-weight: 600; color: var(--bill-accent); margin: 1.25rem 0 .875rem; }
.bill-dialog ::v-deep .v-card__title { color: var(--bill-ink); }
.bill-dialog ::v-deep .v-card__text { font-size: 1rem; color: var(--bill-soft); padding-top: .5rem; }
.bill-dialog ::v-deep .v-card__actions { padding: 1rem 1.5rem; flex-wrap: wrap; gap: .5rem; border-top: 1px solid var(--bill-border); background: rgba(0, 0, 0, .12); }
.bill-dialog ::v-deep .v-btn { font-size: .875rem; border-radius: .625rem; text-transform: none; letter-spacing: 0; max-width: 100%; height: auto; min-height: 36px; padding-top: .5rem; padding-bottom: .5rem; }
.bill-dialog ::v-deep .v-btn__content { white-space: normal; }
.bill-dialog ::v-deep .v-label, .bill-dialog ::v-deep input { font-size: 1rem; }
.bill-dialog ::v-deep .v-messages { font-size: .75rem; }
.bill-trip { display: flex; flex-direction: column; gap: .375rem; padding: 1rem; border: 1px solid var(--bill-border); border-radius: .75rem; background: rgba(255, 255, 255, .035); margin-bottom: 1.25rem; font-size: .875rem; color: var(--bill-soft); }
.bill-trip strong { font-size: 1rem; color: var(--bill-ink); }
.bill-form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 1rem; }
.bill-full { grid-column: 1 / -1; }
.bill-breakdown { margin: 0; }
.bill-breakdown > div, .bill-total { display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; padding: .625rem 0; }
.bill-breakdown dd { margin: 0; text-align: right; font-variant-numeric: tabular-nums; }
.bill-adjust-fields { margin-top: 1.5rem; }
.bill-adjust-switch { margin-bottom: 1.25rem; }
.bill-total { border-top: 1px solid var(--bill-border); margin-top: 1rem; padding: 1rem 0; flex-wrap: wrap; color: var(--bill-ink); }
.bill-total strong { font-size: 1.5rem; font-weight: 600; }
.bill-note { font-size: .875rem; color: var(--bill-muted); line-height: 1.5; margin-bottom: .75rem; }
.bill-status-note { margin-top: 1.25rem; }
@media (max-width: 1260px) { .bill-filters { grid-template-columns: repeat(3, minmax(0, 1fr)); } .bill-search { grid-column: 1 / -1; } .bill-filter-extra { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 1000px) { .bill-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 800px) { .alibey-bills { padding: 1rem; } .bill-header { align-items: flex-start; flex-wrap: wrap; } .bill-filters, .bill-filter-extra { grid-template-columns: 1fr; } .bill-actions { align-items: flex-end; } .bill-form-grid { grid-template-columns: 1fr; } }
@media (max-width: 480px) { .bill-stats article { flex-direction: column; align-items: flex-start; padding: 1rem; } }
@media (max-width: 380px) { .bill-stats { grid-template-columns: 1fr; } }
</style>
